import clsx from 'clsx'
import { ArrowRightIcon } from 'lucide-react'
import React, { useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { SlabAddressFields } from 'components/reusable/form/SlabAddressFields'
import { Button, NumberMask, Select, TelephoneInput, TextButton, TextInput, useInputMask, usePageTitle } from 'slab'
import { validatePOBoxAddressLine1, validatePOBoxAddressLine2 } from 'utils'
import { mergeRefs } from 'utils/ref_utils'
import { usePutEmployersEmployersIdProfile as useUpdateEmployerProfile } from 'utils/web_api'
import { EmployersEmployerBusinessType as EmployerBusinessType, type EmployersEmployerState as EmployerState } from 'utils/web_api.schemas'

import { CenteredLayout } from '../CenteredLayout'
import { useEmployer } from '../EmployerProvider'
import { usePrefill } from '../PrefillProvider'
import { useAPIErrorBanner } from '../useErrorBanner'
import { parseNumber } from '../utils'

const BUSINESS_TYPES: Record<EmployerBusinessType, string> = {
  [EmployerBusinessType.c_corp]: 'C-corporation',
  [EmployerBusinessType.s_corp]: 'S-Corporation',
  [EmployerBusinessType.llc]: 'Limited Liability Company (LLC)',
  [EmployerBusinessType.llp]: 'Limited Liability Partnership (LLP)',
  [EmployerBusinessType.partnership]: 'Partnership',
  [EmployerBusinessType.sole_proprietorship]: 'Sole Proprietorship',
  [EmployerBusinessType.non_profit]: 'Non-Profit',
}

interface RemainingInfoPageProps {
  mapboxAccessToken?: string;
}

function Definition ({
  term,
  definition,
  bold = false,
  optional = false,
}: {
  term: React.ReactNode;
  definition?: React.ReactNode;
  bold?: boolean;
  optional?: boolean;
}) {
  if (optional && !definition) return null

  return (
    <div>
      <dt className='body-sm text-gray-500'>{term}</dt>
      <dd
        className={clsx('text-gray-800', {
          'body-bold': bold,
          body: !bold,
        })}
      >{definition || '—'}
      </dd>
    </div>
  )
}

export function RemainingInfoPage ({ mapboxAccessToken }: RemainingInfoPageProps) {
  usePageTitle('Business information')

  const { employer, refetch: refetchEmployer } = useEmployer()
  const [showPrefilledFields, setShowPrefilledFields] = useState(false)
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()
  const { errorBanner, setError } = useAPIErrorBanner({
    fallbackMessage: 'There was an error updating your business information',
  })
  const { getPrefillValue } = usePrefill()
  const updateEmployerProfile = useUpdateEmployerProfile({
    mutation: {
      onSuccess: async () => {
        await refetchEmployer()
        navigateToNextStep()
      },
      onError: (error) => {
        // TODO: Parse out field errors and add them to form state. That
        // will be a better experience than listing the errors in a banner.
        setError(error)
      }
    }
  })

  const form = useForm({
    defaultValues: {
      address_line1: employer?.address_line1 ?? getPrefillValue('address_line1') ?? '',
      address_line2: employer?.address_line2 ?? '',
      business_type: employer?.business_type ?? getPrefillValue('business_type') ?? '',
      city: employer?.city ?? getPrefillValue('city') ?? '',
      dba: employer?.dba ?? getPrefillValue('dba') ?? '',
      ein: employer?.ein ?? getPrefillValue('ein') ?? '',
      name: employer?.name ?? getPrefillValue('company_name') ?? '',
      number_of_employees: employer?.number_of_employees ?? getPrefillValue('employee_count') ?? '',
      phone_number: employer?.phone_number ?? getPrefillValue('phone_number') ?? '',
      state: employer?.state ?? getPrefillValue('state') ?? '',
      zip: employer?.zip ?? getPrefillValue('zip') ?? '',
    },
    mode: 'onTouched'
  })

  const einInputRef = useRef<HTMLInputElement>(null)
  useInputMask(einInputRef, '99-9999999')

  function onSubmit () {
    const data = form.getValues()

    updateEmployerProfile.mutate({
      id: employer?.id ?? '',
      data: {
        address_line1: data.address_line1,
        address_line2: data.address_line2,
        business_type: data.business_type as EmployerBusinessType,
        city: data.city,
        dba: data.dba,
        ein: data.ein,
        name: data.name,
        number_of_employees: Number(data.number_of_employees),
        phone_number: data.phone_number,
        state: data.state as EmployerState,
        zip: data.zip,
      }
    })
  }

  function renderPrefilledFields () {
    return (
      <div className='flex flex-col sm:flex-row sm:justify-between items-start gap-6 sm:gap-10'>
        <dl className='flex flex-col sm:flex-row gap-4 sm:gap-10 items-start'>
          <Definition term='Business name' definition={employer?.name} bold />
          <Definition term='DBA' definition={employer?.dba} optional />
          <Definition term='Employees' definition={employer?.number_of_employees} />
        </dl>
        <TextButton onClick={() => setShowPrefilledFields(true)}>Edit</TextButton>
      </div>
    )
  }

  function renderEINInput () {
    const { ref, ...registeredFormProps } = form.register('ein', {
      required: 'EIN is required',
      pattern: {
        value: /^\d{2}-\d{7}$/,
        message: 'Must be a 9-digit number with the format XX-XXXXXXX',
      },
    })

    return (
      <TextInput
        {...registeredFormProps}
        ref={mergeRefs(ref, einInputRef)}
        label='Employer Identification Number (EIN)'
        placeholder='xx-xxxxxxx'
        error={form.formState.errors?.ein?.message}
      />
    )
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading='Fill in your business information'
        subheading="We'll need this to ensure your account is set up correctly."
      />
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <CenteredLayout.Container padded={false}>
          <div>
            {!showPrefilledFields && (
              <div className='bg-gray-100 p-6 rounded-t-2xl'>
                {renderPrefilledFields()}
              </div>
            )}
            <div className='p-6 space-y-4'>
              {showPrefilledFields && (
                <>
                  <TextInput
                    {...form.register('name', {
                      required: 'Business name is required',
                    })}
                    label='Business name'
                    placeholder='Enter your business name'
                    error={form.formState.errors?.name?.message}
                  />
                  <div className='grid grid-cols-2 gap-4'>
                    <TextInput
                      {...form.register('dba')}
                      label='DBA (if applicable)'
                    />
                    <NumberMask decimalPlaces={0}>
                      <TextInput
                        {...form.register('number_of_employees', {
                          required: 'Number of employees is required',
                          setValueAs: parseNumber,
                          min: {
                            value: 0,
                            message: 'Must be greater than 0',
                          },
                          max: {
                            value: 1_000_000,
                            message: 'Must be less than 1,000,000',
                          },
                        })}
                        label='Number of employees'
                        placeholder='Enter the number of employees'
                        error={
                          form.formState.errors?.number_of_employees?.message
                        }
                      />
                    </NumberMask>
                  </div>
                </>
              )}
              <SlabAddressFields
                accessToken={mapboxAccessToken ?? ''}
                getValues={form.getValues}
                setValue={form.setValue}
                register={form.register}
                trigger={form.trigger}
                setError={form.setError}
                clearErrors={form.clearErrors}
                errors={form.formState.errors}
                watch={form.watch}
                validationRules={{
                  address_line1: {
                    required: 'Address is required',
                    validate: (value) => {
                      const result = validatePOBoxAddressLine1(String(value), false)
                      if (typeof result === 'string') {
                        window.segment?.track('employer_onboarding_po_box_validation_error', {
                          field: 'address_line1',
                          employer_id: employer?.id,
                        })
                        return 'A physical street address is required for verification purposes; PO boxes cannot be accepted.'
                      }
                    },
                  },
                  address_line2: {
                    validate: (value) => {
                      const result = validatePOBoxAddressLine2(String(value), false)
                      if (typeof result === 'string') {
                        window.segment?.track('employer_onboarding_po_box_validation_error', {
                          field: 'address_line2',
                          employer_id: employer?.id,
                        })
                        return 'A physical street address is required for verification purposes; PO boxes cannot be accepted.'
                      }
                    },
                  },
                  city: {
                    required: 'City is required',
                  },
                  state: {
                    required: 'State is required',
                  },
                  zip: {
                    required: 'Zip code is required',
                  },
                }}
              />
              <Select
                {...form.register('business_type', {
                  required: 'Business type is required',
                })}
                label='Business type'
                placeholder='Select one'
                error={form.formState.errors?.business_type?.message}
              >
                {Object.keys(EmployerBusinessType)
                  .filter((key) => key !== 'non_profit')
                  .map((key) => (
                    <option key={key} value={key}>
                      {BUSINESS_TYPES[key as EmployerBusinessType]}
                    </option>
                  ))}
              </Select>
              <div className='grid xs:grid-cols-2 gap-4'>
                <TelephoneInput
                  {...form.register('phone_number', {
                    required: 'Phone number is required',
                  })}
                  label='Business phone number'
                  placeholder='Enter your phone number'
                  error={form.formState.errors?.phone_number?.message}
                />
                {renderEINInput()}
              </div>
              {errorBanner}
            </div>
          </div>
        </CenteredLayout.Container>
        <CenteredLayout.Buttons>
          <Button variant='secondary' onClick={navigateToPreviousStep}>
            Back
          </Button>
          <Button
            type='submit'
            variant='primary'
            rightIcon={ArrowRightIcon}
            disabled={updateEmployerProfile.isPending}
          >
            Confirm and continue
          </Button>
        </CenteredLayout.Buttons>
      </form>
    </CenteredLayout>
  )
}
